import { buttonStyle } from 'components/buttons/buttonStyle';
import { CREATE_DIALOG_OTHER_ACTION, CreateDialogModalFrame } from 'components/create-dialog/CreateDialogModalFrame';
import { CreateDialogRequiredBlock } from 'components/create-dialog/CreateDialogRequired';
import { CreateDialogSection } from 'components/create-dialog/CreateDialogSection';
import DecimalInput from 'components/forms/inputs/rhf-inputs/DecimalInput';
import PercentageInput from 'components/forms/inputs/rhf-inputs/PercentageInput';
import TextInput from 'components/forms/inputs/rhf-inputs/TextInput';
import { ModalFooterSimple } from 'components/modals/ModalFooterSimple';
import { ModalHeader } from 'components/modals/ModalHeader';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { Button } from 'components/ui/button';
import { formatAmount } from 'components/util/MoneyDisplay';
import { useInvoice } from 'pages/invoices/hooks/useInvoice';
import { useSharedPayment } from 'pages/invoices/payers/useSharedPayment';
import { useCallback, useMemo } from 'react';
import { useForm, useWatch, ValidateResult } from 'react-hook-form';
import { useGetInvoiceTotalsQuery } from 'store/api/invoices';
import { InvoiceData, InvoiceRowForm, InvoiceRowTotals } from 'types/invoices';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { paymentCurrencyInputClassName, usePaymentCurrencyMode } from 'utils/usePaymentCurrencyMode';
import PaymentCurrencyNotice from '../../components/PaymentCurrencyNotice';
import { DetailsAlert } from '../../details-tab/Details';
import { FEE_AMOUNT_FIELD, FEE_DETAILS_SECTION, feeDialogRecord } from '../feeDialogRecord';

interface Props {
    action: 'create' | 'update';
    initialData: InvoiceRowForm;
    onClose: () => void;
    onSubmit: (data: InvoiceRowForm) => void;
    onDelete?: (invoiceRowId: number) => void;
    invoiceRowTotals?: InvoiceRowTotals;
    invoice: InvoiceData;
}

export const InvoiceRowEditModal = (props: Props) => {
    const { action, onClose, onSubmit, onDelete, invoiceRowTotals, initialData, invoice } = props;
    const isInViewMode = !invoice.isEditable;
    const { data: invoiceTotals } = useGetInvoiceTotalsQuery(invoice.id);
    const { isPaymentCurrencyMode, displayCurrency } = usePaymentCurrencyMode(invoice);
    const { isCredit } = useInvoice(invoice);
    const isBlankCreditNote = isCredit && !invoice.debitInvoice;
    // A shared-payment copy's amounts are the group's: its rows keep their amounts and only reword.
    const { isSharedPaymentCopy: isDescriptionOnly } = useSharedPayment(invoice);

    const {
        handleSubmit,
        control,
        setValue,
        getValues,
        formState: { errors },
    } = useForm<InvoiceRowForm>({
        defaultValues: initialData,
        values: initialData,
        mode: 'onChange',
    });

    // The amount the row has to stay above for the invoice's total without VAT to stay above zero; none on a blank
    // credit note, whose rows are all negative.
    const amountFloor = useMemo(() => {
        if (!invoiceTotals || isBlankCreditNote) {
            return null;
        }
        const totals = isPaymentCurrencyMode ? invoiceTotals.paymentCurrency : invoiceTotals.invoiceCurrency;
        const existingRowTotal = action === 'update' ? (initialData.itemTotal ?? 0) : 0;
        return existingRowTotal - (totals?.invoice?.totalWithoutVat ?? 0);
    }, [invoiceTotals, action, initialData.itemTotal, isPaymentCurrencyMode, isBlankCreditNote]);

    const isValidAmountBeforeDiscount = useCallback(
        (value: number | string | undefined): ValidateResult =>
            !value || isNaN(Number(value)) || amountFloor === null || Number(value) > amountFloor,
        [amountFloor]
    );

    const [amountBeforeDiscount] = useWatch({ control, name: ['amountBeforeDiscount'] });
    const isAmountMissing =
        amountBeforeDiscount !== 0 && (!amountBeforeDiscount || isNaN(Number(amountBeforeDiscount)));
    const isNegativeAmount = useMemo(() => !!amountBeforeDiscount && amountBeforeDiscount < 0, [amountBeforeDiscount]);
    const isTotalAmountExceeded = errors.amountBeforeDiscount && isNegativeAmount;

    const handleOnSubmit = (data: InvoiceRowForm) => {
        onSubmit(data);
        onClose();
    };

    const onAmountBeforeDiscountChange = useCallback(
        (value: string | undefined) => {
            const discountRate: number | undefined = getValues('discountRate');
            if (value && !isNaN(Number(value))) {
                const amountBeforeDiscount = Number(value);
                if (amountBeforeDiscount < 0) {
                    setValue('discountRate', 0);
                }
                const discountAmount =
                    !discountRate || amountBeforeDiscount < 0
                        ? 0
                        : Math.round(((amountBeforeDiscount * discountRate) / 100) * 100) / 100;
                const itemTotal = amountBeforeDiscount - discountAmount;
                setValue('itemTotal', Math.round(itemTotal * 100) / 100);
                setValue('discountAmount', discountAmount === 0 ? '0' : (discountAmount * -1).toFixed(2));
            } else {
                setValue('itemTotal', 0);
                setValue('discountAmount', '0');
            }
        },
        [setValue, getValues]
    );

    const onDiscountRateChange = useCallback(
        (value: string | undefined) => {
            const amountBeforeDiscount: number | undefined = getValues('amountBeforeDiscount');
            if (value && !isNaN(Number(value))) {
                const discountRate = Number(value);
                const discountAmount = Math.round((((amountBeforeDiscount ?? 0) * discountRate) / 100) * 100) / 100;
                const itemTotal = (amountBeforeDiscount ?? 0) - discountAmount;
                setValue('itemTotal', Math.round(itemTotal * 100) / 100);
                setValue('discountAmount', discountAmount === 0 ? '0' : (discountAmount * -1).toFixed(2));
            } else {
                setValue('itemTotal', amountBeforeDiscount || 0);
                setValue('discountAmount', '0');
            }
        },
        [setValue, getValues]
    );

    const onDiscountAmountChange = useCallback(
        (value: string | undefined) => {
            const amountBeforeDiscount: number | undefined = getValues('amountBeforeDiscount');
            if (value && !isNaN(Number(value)) && amountBeforeDiscount) {
                const discountAmount = Math.abs(Number(value));
                const itemTotal = (amountBeforeDiscount ?? 0) - discountAmount;
                setValue('itemTotal', Math.round(itemTotal * 100) / 100);
                const disaountRate = (discountAmount / amountBeforeDiscount) * 100;
                const roundingValue = Math.pow(10, 7);
                setValue('discountRate', Math.round(disaountRate * roundingValue) / roundingValue);
            } else {
                setValue('itemTotal', 0);
                setValue('discountRate', 0);
            }
        },
        [setValue, getValues]
    );

    const isNewDesign = useIsNewDesign();
    const feeRecord = feeDialogRecord(invoice, initialData.matterId);
    const isDeletable = !!onDelete && !!invoiceRowTotals && !isInViewMode && !isDescriptionOnly;
    const handleDelete = () => {
        if (onDelete && invoiceRowTotals) {
            onDelete(invoiceRowTotals.invoiceRowId);
            onClose();
        }
    };
    const submit = () => {
        void handleSubmit(handleOnSubmit)();
    };

    // The new design's Details: the description on its own row, then the two discounts; amounts at the end.
    const amountProps = isNewDesign ? FEE_AMOUNT_FIELD : {};
    const descriptionField = (
        <TextInput
            control={control}
            name="description"
            label="invoices.totals-tab.description"
            wrapperClassName={isNewDesign ? 'col-span-full' : undefined}
        />
    );
    const amountField = (
        <DecimalInput
            control={control}
            {...amountProps}
            allowNegative
            onlyNegative={isBlankCreditNote}
            name="amountBeforeDiscount"
            label="invoices.totals-tab.amount-wo-vat"
            onChange={(event) => onAmountBeforeDiscountChange(event.target.value)}
            validate={isValidAmountBeforeDiscount}
        />
    );
    const discountRateField = (
        <PercentageInput
            control={control}
            name="discountRate"
            wrapperClassName={isNewDesign ? FEE_AMOUNT_FIELD.wrapperClassName : undefined}
            decimalPlaces={7}
            label="invoices.totals-tab.discount"
            onChange={(event) => onDiscountRateChange(event.target.value)}
            disabled={isNegativeAmount}
            validate={(value) => {
                if (typeof value === 'number' && value > 100) {
                    return t('validation.cannot-exceed-pct', { max: '100' });
                }
                return true;
            }}
        />
    );
    const discountAmountField = (
        <DecimalInput
            control={control}
            {...amountProps}
            onlyNegative
            name="discountAmount"
            label={t('invoices.totals-tab.discount-currency', { currency: displayCurrency })}
            shouldTranslateLabel={false}
            onChange={(event) => onDiscountAmountChange(event.target.value)}
            disabled={isNegativeAmount}
        />
    );
    const totalField = (
        <TextInput
            control={control}
            label="invoices.totals-tab.total-wo-vat"
            name="itemTotal"
            disabled
            required
            {...amountProps}
        />
    );

    if (isNewDesign) {
        return (
            <CreateDialogModalFrame
                {...feeRecord}
                title={t(`invoices.totals-tab.${action}-other-fee`)}
                notice={isPaymentCurrencyMode ? <PaymentCurrencyNotice currency={displayCurrency} /> : null}
                openSection={FEE_DETAILS_SECTION}
                onCancel={onClose}
                otherActions={
                    isDeletable ? (
                        <Button {...CREATE_DIALOG_OTHER_ACTION} onClick={handleDelete}>
                            {t('delete')}
                        </Button>
                    ) : null
                }
                mainAction={
                    isInViewMode
                        ? undefined
                        : {
                              label: t(action === 'create' ? 'invoices.totals-tab.create' : 'save'),
                              onClick: submit,
                              disabled: isAmountMissing,
                          }
                }
            >
                <CreateDialogSection title={FEE_DETAILS_SECTION}>
                    {isDescriptionOnly ? null : (
                        <>
                            {/* The amount is what the fee is: the dialog asks for it first, starred, though the
                                form only refuses it empty by its Create, which waits for it. */}
                            <CreateDialogRequiredBlock when starred>
                                {amountField}
                            </CreateDialogRequiredBlock>
                            {totalField}
                            <CreateDialogRequiredBlock when>
                                {isTotalAmountExceeded ? (
                                    <DetailsAlert
                                        tone="error"
                                        description={t('validation.negative-other-fee-floor', {
                                            amount: formatAmount(amountFloor ?? 0),
                                            isCreate: String(action === 'create'),
                                        })}
                                    >
                                        {t('validation.negative-other-fee-exceeds-total')}
                                    </DetailsAlert>
                                ) : null}
                            </CreateDialogRequiredBlock>
                        </>
                    )}
                    {descriptionField}
                    {isDescriptionOnly ? null : (
                        <>
                            {discountRateField}
                            {discountAmountField}
                        </>
                    )}
                </CreateDialogSection>
            </CreateDialogModalFrame>
        );
    }

    const actions = [
        <Button key="cancel" {...buttonStyle({ variant: 'outlined', color: 'primary' })} onClick={onClose}>
            {t('cancel')}
        </Button>,
    ];

    if (!isInViewMode) {
        actions.push(
            <Button
                key="update-fee"
                {...buttonStyle({ variant: 'contained', color: 'secondary' })}
                disabled={isAmountMissing}
                onClick={submit}
            >
                {t(`invoices.totals-tab.${action}`)}
            </Button>
        );
    }

    if (isDeletable) {
        actions.push(
            <Button key="delete" {...buttonStyle({ variant: 'outlined', color: 'primary' })} onClick={handleDelete}>
                {t('delete')}
            </Button>
        );
    }

    return (
        <>
            <ModalHeader title={t(`invoices.totals-tab.${action}-other-fee`)} onCancel={onClose} />
            <div className={cn('h-full overflow-y-auto p-6 max-sm:px-4', isTotalAmountExceeded ? 'pb-1.5' : 'pb-8')}>
                {isPaymentCurrencyMode && <PaymentCurrencyNotice currency={displayCurrency} className="mb-4" />}
                <div className="grid grid-cols-12 gap-4">
                    <div className="col-span-12">{descriptionField}</div>
                    {isDescriptionOnly ? null : (
                        <>
                            <div className={cn('col-span-3', isPaymentCurrencyMode && paymentCurrencyInputClassName)}>
                                {amountField}
                            </div>
                            <div className={cn('col-span-3', isPaymentCurrencyMode && paymentCurrencyInputClassName)}>
                                {discountRateField}
                            </div>
                            <div className={cn('col-span-3', isPaymentCurrencyMode && paymentCurrencyInputClassName)}>
                                {discountAmountField}
                            </div>
                            <div className={cn('col-span-3', isPaymentCurrencyMode && paymentCurrencyInputClassName)}>
                                {totalField}
                            </div>
                        </>
                    )}
                </div>
                {isTotalAmountExceeded && (
                    <p className="text-caption text-error mt-2">{t('validation.negative-other-fee-exceeds-total')}</p>
                )}
            </div>
            <ModalFooterSimple buttons={actions} />
        </>
    );
};
